// Modal dialog and sheet (UX01, UX03). Owner: WP04. Built on native <dialog>: focus trap, Escape and inert background for free.
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button, IconButton } from './Button.tsx';

export type DialogProps = {
  open: boolean;
  onClose(): void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  /** Footer actions (buttons). */
  actions?: ReactNode;
  /** 'dialog' = centred; 'sheet' = full-screen on mobile, right-side panel on desktop (detail sheets). */
  variant?: 'dialog' | 'sheet';
  /** Hide the visible title (still labels the dialog). */
  hideTitle?: boolean;
};

export function Dialog({ open, onClose, title, description, children, actions, variant = 'dialog', hideTitle }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const el = ref.current;
    if (!el || !open) return;
    const opener = document.activeElement as HTMLElement | null;
    if (typeof el.showModal === 'function') el.showModal();
    else el.setAttribute('open', '');
    const onCancel = (e: Event) => { e.preventDefault(); onCloseRef.current(); };
    el.addEventListener('cancel', onCancel);
    return () => {
      el.removeEventListener('cancel', onCancel);
      if (typeof el.close === 'function' && el.open) el.close();
      else el.removeAttribute('open');
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <dialog
      ref={ref}
      className={`ui-dialog ui-dialog--${variant}`}
      aria-labelledby={`${id}-title`}
      aria-describedby={description ? `${id}-desc` : undefined}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ui-dialog__panel">
        <header className="ui-dialog__header">
          <h2 id={`${id}-title`} className={hideTitle ? 'visually-hidden' : undefined}>{title}</h2>
          <IconButton label="Close" icon={<X size={20} />} onClick={onClose} />
        </header>
        {description ? <div id={`${id}-desc`} className="ui-dialog__desc">{description}</div> : null}
        {children ? <div className="ui-dialog__body">{children}</div> : null}
        {actions ? <footer className="ui-dialog__actions">{actions}</footer> : null}
      </div>
    </dialog>
  );
}

export type ConfirmDialogProps = {
  open: boolean;
  title: string;
  /** What will happen, in plain words. */
  body: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm(): void;
  onCancel(): void;
};

/** Confirmation for destructive or irreversible actions. Cancel is the default focus. */
export function ConfirmDialog({ open, title, body, confirmLabel, destructive, busy, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      description={body}
      actions={
        <>
          <Button variant="ghost" onClick={onCancel} autoFocus>Cancel</Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm} loading={busy}>{confirmLabel}</Button>
        </>
      }
    />
  );
}
